import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner, Alert } from "flowbite-react";
import { HiExclamationCircle } from "react-icons/hi";

import { useSensitivity, useActionsConfig, useToast } from "../../../hooks";
import { NOTIFICATION_TYPES } from "../../../utils";
import { SensitivityCategory } from "./SensitivityCategory";
import { SeasonalitySection } from "./SeasonalitySection";

const SENSITIVITY_CATEGORIES = [
  "product_usage",
  "communication",
  "contract_metrics",
  "csat_nps",
];

export const ActionsSettings = () => {
  const { t } = useTranslation();
  const showToast = useToast();

  // Sensitivity state
  const {
    config: sensitivityConfig,
    loading: sensitivityLoading,
    saving: sensitivitySaving,
    error: sensitivityError,
    updateConfig: updateSensitivity,
    refetch: refetchSensitivity,
  } = useSensitivity();

  // Actions config state (for date ranges and weekends)
  const {
    config: actionsConfig,
    loading: actionsLoading,
    saving: actionsSaving,
    error: actionsError,
    updateConfig: updateActionsConfig,
    refetch: refetchActions,
  } = useActionsConfig();

  const [sensitivityFormState, setSensitivityFormState] = useState(null);
  const [hasSensitivityChanges, setHasSensitivityChanges] = useState(false);

  const [dateRangesFormState, setDateRangesFormState] = useState(null);
  const [excludeWeekendsFormState, setExcludeWeekendsFormState] =
    useState(false);
  const [hasActionsConfigChanges, setHasActionsConfigChanges] = useState(false);

  // Initialize sensitivity form state
  useEffect(() => {
    if (sensitivityConfig && !sensitivityFormState) {
      setSensitivityFormState(sensitivityConfig);
    }
  }, [sensitivityConfig, sensitivityFormState]);

  // Initialize actions config form state (date ranges + weekends)
  useEffect(() => {
    if (actionsConfig && dateRangesFormState === null) {
      setDateRangesFormState(actionsConfig.exclude_date_ranges || []);
      setExcludeWeekendsFormState(actionsConfig.exclude_weekends || false);
    }
  }, [actionsConfig, dateRangesFormState]);

  // Track sensitivity changes
  useEffect(() => {
    if (sensitivityFormState && sensitivityConfig) {
      const changed =
        JSON.stringify(sensitivityFormState) !==
        JSON.stringify(sensitivityConfig);
      setHasSensitivityChanges(changed);
    }
  }, [sensitivityFormState, sensitivityConfig]);

  // Track actions config changes (date ranges + weekends)
  useEffect(() => {
    if (dateRangesFormState !== null && actionsConfig) {
      const rangesChanged =
        JSON.stringify(dateRangesFormState) !==
        JSON.stringify(actionsConfig.exclude_date_ranges || []);
      const weekendsChanged =
        excludeWeekendsFormState !== (actionsConfig.exclude_weekends || false);
      setHasActionsConfigChanges(rangesChanged || weekendsChanged);
    }
  }, [dateRangesFormState, excludeWeekendsFormState, actionsConfig]);

  const handleSensitivityChange = (categoryKey, signalKey, newValue) => {
    setSensitivityFormState((prev) => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        [signalKey]: newValue,
      },
    }));
  };

  const handleDateRangeUpdate = (index, updatedRange) => {
    const newRanges = [...dateRangesFormState];
    newRanges[index] = updatedRange;
    setDateRangesFormState(newRanges);
  };

  const handleDateRangeAdd = () => {
    const newRanges = [
      ...dateRangesFormState,
      { id: `new-${Date.now()}`, name: "", start: "", end: "" },
    ];
    setDateRangesFormState(newRanges);
  };

  const handleDateRangeRemove = (index) => {
    const newRanges = dateRangesFormState.filter((_, i) => i !== index);
    setDateRangesFormState(newRanges);
  };

  const handleToggleWeekends = (checked) => {
    setExcludeWeekendsFormState(checked);
  };

  const loading = sensitivityLoading || actionsLoading;
  const error = sensitivityError || actionsError;

  if (loading) {
    return (
      <div className="flex h-[32rem] w-full items-center justify-center">
        <Spinner aria-label="Loading configuration" size="xl" />
      </div>
    );
  }

  if (error && !sensitivityFormState && dateRangesFormState === null) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <Alert color="failure" icon={HiExclamationCircle}>
          <span>{error}</span>
        </Alert>
        <Button
          color="blue"
          onClick={() => {
            refetchSensitivity();
            refetchActions();
          }}
        >
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  const hasAnyChanges = hasSensitivityChanges || hasActionsConfigChanges;
  const isSaving = sensitivitySaving || actionsSaving;

  const handleSaveAll = async () => {
    try {
      const promises = [];
      if (hasSensitivityChanges) {
        promises.push(updateSensitivity(sensitivityFormState));
      }
      if (hasActionsConfigChanges) {
        promises.push(
          updateActionsConfig({
            exclude_date_ranges: dateRangesFormState,
            exclude_weekends: excludeWeekendsFormState,
          }),
        );
      }
      await Promise.all(promises);
      showToast({
        content: t("pages.settings.actionsSettings.saveSuccess"),
        type: NOTIFICATION_TYPES.success,
      });
    } catch {
      showToast({
        content: t("pages.settings.actionsSettings.saveError"),
        type: NOTIFICATION_TYPES.error,
      });
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col gap-6">
      {/* Sensitivity Section */}
      {sensitivityFormState && (
        <div className="animation-slide-up-lil-bounce flex max-w-2xl flex-col rounded-lg bg-white p-6 shadow">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {t("pages.settings.actionsSettings.title")}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("pages.settings.actionsSettings.description")}
            </p>
          </div>

          <div className="space-y-4">
            {SENSITIVITY_CATEGORIES.map((categoryKey) => (
              <SensitivityCategory
                key={categoryKey}
                categoryKey={categoryKey}
                categoryValues={sensitivityFormState[categoryKey]}
                onChange={handleSensitivityChange}
                disabled={sensitivitySaving}
              />
            ))}
          </div>
        </div>
      )}

      {/* Seasonality Section */}
      {dateRangesFormState !== null && (
        <div className="animation-slide-up-lil-bounce flex max-w-2xl flex-col rounded-lg bg-white p-6 shadow">
          <SeasonalitySection
            dateRanges={dateRangesFormState}
            excludeWeekends={excludeWeekendsFormState}
            onUpdate={handleDateRangeUpdate}
            onAdd={handleDateRangeAdd}
            onRemove={handleDateRangeRemove}
            onToggleWeekends={handleToggleWeekends}
            disabled={actionsSaving}
          />
        </div>
      )}

      {/* Save Button */}
      {hasAnyChanges && (
        <div className="max-w-2xl">
          <Button
            color="blue"
            onClick={handleSaveAll}
            disabled={isSaving}
            isProcessing={isSaving}
          >
            {t("pages.settings.actionsSettings.save")}
          </Button>
        </div>
      )}
    </div>
  );
};
